import React from 'react';

// MUI
import { Grid, styled, Tooltip, Alert, Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Util
import { Link } from 'react-router-dom';
import { format } from '../../../../utils/Date';
import useProjectStore from '../../useProjectStore';
import DraftProductionSwitch from './DraftProductionSwitch';

const VersionText = styled('span')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 400,
  color: theme.palette.text.secondary,
}));

const UnpublishedAlert = styled(Alert)(({ theme }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: `${theme.palette.secondary.main}14`,
  color: theme.palette.secondary.main,
  '& .MuiAlert-icon': {
    color: theme.palette.secondary.main,
    padding: 0,
    marginRight: '8px',
  },
  '& .MuiAlert-message': {
    padding: 0,
    fontSize: '13px',
    fontWeight: 500,
  },
  '& svg': {
    fontSize: '18px',
  },
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: `${theme.palette.secondary.main}1F`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

function DraftProductionContent(props) {
  const {
    changes,
    production,
    isCanvasLocked,
    draftVersion,
    lastModified,
    publishedVersion,
    projectId,
  } = props;

  const getVersionBehindText = (draftVersion, version, projectId) => {
    if (!draftVersion || !version) return;
    if (version === 'None') return;
    let versionDiff = parseInt(draftVersion - version);

    switch (versionDiff) {
      case 0:
        return (
          <>
            Same version as{' '}
            <Link
              to={`/project/production/${projectId}`}
              style={{ textDecoration: 'none', color: 'var(--color-info)' }}
            >
              Production
            </Link>
          </>
        );
      case 1:
        return (
          <>
            1 version ahead of{' '}
            <Link
              to={`/project/production/${projectId}`}
              style={{ textDecoration: 'none', color: 'var(--color-info)' }}
            >
              Production
            </Link>{' '}
          </>
        );
      default:
        return (
          <>
            {versionDiff} versions ahead of{' '}
            <Link
              to={`/project/production/${projectId}`}
              style={{ textDecoration: 'none', color: 'var(--color-info)' }}
            >
              Production
            </Link>
          </>
        );
    }
  };

  return (
    <Grid
      container
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      spacing={1}
      wrap="nowrap"
    >
      <Grid item>
        <DraftProductionSwitch
          isDraft
          hasPublishedVersion={publishedVersion !== 'None'}
          projectId={projectId}
        />
      </Grid>
      <Grid item>
        <Tooltip title={lastModified ? format(lastModified, useProjectStore.getState().timezone) : ''}>
          <VersionText>
            Version {draftVersion}{' '}
            {lastModified &&
              ` saved ${format(lastModified, useProjectStore.getState().timezone, 'relative')}`}
          </VersionText>
        </Tooltip>
      </Grid>
      <Grid item>
        {changes && (
          <UnpublishedAlert severity="warning" icon={<WarningAmberIcon />}>
            Unpublished changes
          </UnpublishedAlert>
        )}
      </Grid>
    </Grid>
  );
}

export default DraftProductionContent;
